<template lang="pug">
.file-field
  .form-field-label(v-if="label")
    span {{ label }}
    span.form-field-require-tag(v-show="required", :title="notifies.required_field")
      | *

  .form-field.form-file(:class="[invalidClass, dragClass]")
    .file-field__drop-area(
      v-if="!hasSelectedFiles",
      @click="openFileInput",
      @dragenter.prevent="startDrag",
      @dragover.prevent="startDrag",
      @dragleave.prevent="finishDrag",
      @drop.prevent="dropFiles"
    )
      img.file-field__icon(:src="require('../../../../assets/icons/form/fields/file_field_icon.svg')")
      span.file-field__placeholder {{ placeholderText }}

    .file-field__preview(
      v-else,
      @click="openFileInput",
      @dragenter.prevent="startDrag",
      @dragover.prevent="startDrag",
      @dragleave.prevent="finishDrag",
      @drop.prevent="dropFiles"
    )
      .file-field__preview-file(v-for="file in selectedFiles", :key="getFileKey(file)")
        img.file-field__preview-img(v-if="isImageFile(file)", :src="getFilePreview(file)", :alt="getFileName(file)")
        inline-svg.file-field__preview-doc(v-else, :src="require('../../../../assets/icons/document.svg')")
        span.file-field__caption(:title="getFileName(file)") {{ cutFileName(getFileName(file)) }}

    input.file-field__input(
      ref="fileInputRef",
      type="file",
      :name="name",
      :multiple="multiple",
      :accept="accept",
      :disabled="readonly",
      @change="changeFiles"
    )

    q-icon.cancel-file-field(name="cancel", v-if="hasSelectedFiles && !readonly", @click.stop="resetValue")

  .valid-error-message(v-if="invalid") {{ errorMessage }}

  .file-field__existing(v-if="hasExistingFiles")
    existing-files-area(:parentData="{ files: existingFiles }")
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { fileFieldLocales, notifies } from "@/services/useLocales";
import { Notify } from "quasar";
import existingFilesArea from "../../helpers/existingFilesArea.vue";

const props = defineProps({
  /** Текст label над полем. */
  label: { type: String, default: "", required: false },
  /** Флаг обязательности поля и отображения *. */
  required: { type: Boolean, default: false, required: false },
  /** Начальное значение поля: File, массив File, строка или объект уже загруженного файла. */
  initialValue: { type: [Object, Array, String, File, Blob], default: null, required: false },
  /** Уже загруженные файлы для отображения через existingFilesArea. */
  existingValue: { type: [Object, Array, String], default: null, required: false },
  /** Совместимость с релизным API fileField: список уже загруженных файлов. */
  loadedFiles: { type: Array, default: () => [], required: false },
  /** Уникальное имя инпута для мест, где оно уже передавалось. */
  name: { type: String, default: "file-field", required: false },
  /** Текст внутри пустой зоны загрузки. */
  placeholder: { type: String, default: "", required: false },
  /** Флаг readonly, запрещающий выбор и сброс файла. */
  readonly: { type: Boolean, default: false, required: false },
  /** Флаг невалидности поля. */
  invalid: { type: Boolean, default: false, required: false },
  /** Текст ошибки валидации. */
  errorMessage: { type: String, default: "", required: false },
  /** Разрешённые типы файлов для input accept. */
  accept: { type: String, default: "", required: false },
  /** Флаг множественного выбора файлов. */
  multiple: { type: Boolean, default: false, required: false },
});

const emit = defineEmits([
  /** Эмит изменения выбранного файла. */
  "change",
  /** Эмит сброса выбранного файла. */
  "reset",
]);

const fileInputRef = ref(null);
const value = ref(null);
const dragActive = ref(false);
const previewUrls = ref({});

const invalidClass = computed(() => (props.invalid ? "valid-error" : ""));
const dragClass = computed(() => (dragActive.value ? "file-field--drag-active" : ""));
const placeholderText = computed(() => props.placeholder || fileFieldLocales.value?.sublabel || "");
const selectedFiles = computed(() => normalizeFiles(value.value));
const hasSelectedFiles = computed(() => selectedFiles.value.length > 0);
const existingFiles = computed(() => {
  const files = [
    ...normalizeFiles(props.existingValue),
    ...normalizeFiles(props.loadedFiles),
    ...normalizeFiles(props.initialValue).filter(file => !isFileObject(file)),
  ];
  const uniqueFiles = new Map();

  files.forEach(file => {
    const normalizedFile = normalizeExistingFile(file);

    if (normalizedFile) {
      uniqueFiles.set(getFileKey(normalizedFile), normalizedFile);
    }
  });

  return [...uniqueFiles.values()];
});
const hasExistingFiles = computed(() => existingFiles.value.length > 0);

const normalizeFiles = files => {
  if (!files) {
    return [];
  }

  return [files].flat().filter(file => file);
};

const getInitialSelectedValue = initialValue => {
  const files = normalizeFiles(initialValue).filter(isFileObject);

  if (!files.length) {
    return props.multiple ? [] : null;
  }

  return props.multiple ? files : files[0];
};

const getFileName = file => {
  if (typeof file === "string") {
    return file;
  }

  return file?.name ?? file?.title ?? file?.file_name ?? file?.filename ?? "";
};

const getFileKey = file => {
  if (typeof file === "string") {
    return file;
  }

  return file?.id ?? file?.url ?? file?.path ?? `${getFileName(file)}-${file?.size ?? ""}-${file?.lastModified ?? ""}`;
};

const isFileObject = file => typeof File !== "undefined" && file instanceof File;

const normalizeExistingFile = file => {
  if (!file || isFileObject(file)) {
    return null;
  }

  if (typeof file === "string") {
    return {
      url: file,
      file_name: file.split("/").pop() || file,
      content_type: "",
    };
  }

  return {
    ...file,
    url: file.url ?? file.path ?? "",
    file_name: getFileName(file),
    content_type: file.content_type ?? file.type ?? "",
  };
};

value.value = getInitialSelectedValue(props.initialValue);

const isImageFile = file => {
  if (isFileObject(file)) {
    return file.type.startsWith("image/");
  }

  const contentType = file?.content_type ?? file?.type ?? "";
  return contentType.startsWith("image/");
};

const getFilePreview = file => {
  if (!isFileObject(file)) {
    return file?.url ?? file?.path ?? "";
  }

  const key = getFileKey(file);

  if (!previewUrls.value[key]) {
    previewUrls.value = {
      ...previewUrls.value,
      [key]: URL.createObjectURL(file),
    };
  }

  return previewUrls.value[key];
};

const revokePreviewUrls = () => {
  Object.values(previewUrls.value).forEach(url => {
    URL.revokeObjectURL(url);
  });
  previewUrls.value = {};
};

const cutFileName = name => {
  if (!name || name.length <= 24) {
    return name;
  }

  const extensionMatch = name.match(/\.[^/.]+$/);
  const extension = extensionMatch?.[0] ?? "";

  return `${name.substring(0, 18)}..${extension}`;
};

const parseAcceptList = () =>
  props.accept
    .split(",")
    .map(item => item.trim())
    .filter(item => item);

const fileMatchesAccept = file => {
  const acceptList = parseAcceptList();

  if (!acceptList.length) {
    return true;
  }

  return acceptList.some(acceptType => {
    if (acceptType.endsWith("/*")) {
      return file.type.startsWith(acceptType.replace("/*", "/"));
    }

    if (acceptType.startsWith(".")) {
      return getFileName(file).toLowerCase().endsWith(acceptType.toLowerCase());
    }

    return file.type === acceptType;
  });
};

const notifyInvalidFiles = invalidFiles => {
  if (!invalidFiles.length) {
    return;
  }

  Notify.create({
    badgeStyle: "display: none;",
    message: `${notifies.value.invalid_file_extension}${props.accept}`,
  });
};

const prepareFiles = fileList => {
  const files = Array.from(fileList ?? []).filter(file => file);
  const validFiles = files.filter(fileMatchesAccept);
  const invalidFiles = files.filter(file => !fileMatchesAccept(file));

  notifyInvalidFiles(invalidFiles);

  return props.multiple ? validFiles : validFiles.slice(0, 1);
};

const setFiles = files => {
  const nextValue = props.multiple ? files : files[0] ?? null;

  revokePreviewUrls();
  value.value = nextValue;
  emit("change", { value: nextValue });
};

const openFileInput = () => {
  if (props.readonly) {
    return;
  }

  fileInputRef.value?.click();
};

const changeFiles = event => {
  setFiles(prepareFiles(event?.target?.files));
};

const startDrag = () => {
  if (props.readonly) {
    return;
  }

  dragActive.value = true;
};

const finishDrag = () => {
  dragActive.value = false;
};

const dropFiles = event => {
  if (props.readonly) {
    return;
  }

  finishDrag();
  setFiles(prepareFiles(event?.dataTransfer?.files));
};

const resetValue = () => {
  revokePreviewUrls();
  value.value = props.multiple ? [] : null;

  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }

  emit("reset");
  emit("change", { value: value.value });
};

watch(
  () => props.initialValue,
  newValue => {
    revokePreviewUrls();
    value.value = getInitialSelectedValue(newValue);
  },
  { deep: true },
);

onBeforeUnmount(() => {
  revokePreviewUrls();
});
</script>

<script>
export default {
  name: "FileField",
};
</script>

<style scoped lang="scss">
.file-field {
  &__drop-area {
    width: 100%;
    min-height: 140px;
    padding: 42px 20px;
    border: var(--file-field-border);
    border-radius: 8px;
    color: var(--field-input-color);
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    span {
      display: block;
    }
  }

  &__icon {
    margin-bottom: 8px;
  }

  &__preview {
    width: 100%;
    padding: 20px 10px;
    border: var(--field-border);
    border-radius: 8px;
    background-color: var(--field-background);
    color: var(--field-input-color);
    text-align: center;
    cursor: pointer;
  }

  &__preview-file {
    width: 13em;
    padding: 1em;
    display: inline-block;
    text-align: center;
    vertical-align: top;
  }

  &__preview-img {
    height: 5em;
    max-width: 100%;
    object-fit: contain;
  }

  &__preview-doc {
    width: auto;
    height: 5em;

    :deep(path) {
      fill: var(--existing-file-fill);
    }
  }

  &__caption {
    display: block;
    margin-top: 8px;
  }

  &__input {
    display: none;
  }

  .form-file {
    position: relative;
    background-color: var(--field-background);
  }

  .cancel-file-field {
    position: absolute;
    top: 15px;
    right: 17px;
    color: var(--field-cancel-icon-color);
    cursor: pointer;
  }

  &--drag-active {
    .file-field__drop-area,
    .file-field__preview {
      background: rgb(91 122 255 / 0.08);
      border-color: #5b7aff;
    }
  }
}
</style>
